import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import DepositCss from '../css/DepositCss';
import { useNavigate } from 'react-router-dom';
import { Context } from '../Context/Context';
import { Flip, toast } from 'react-toastify';
import { EditClientApi } from '../Api/EditClient';
import { GetAlClientsApi } from '../Api/GetAllClients';
import { Helmet } from 'react-helmet';
export default function EditClient() {
  const navigate = useNavigate();
  const { setSessionDetail, setLoad, setLoadColor } = useContext(Context);

  const [clientList, setClientList] = useState([]);
  const [selectedClientName, setSelectedClientName] = useState('');
  const [clientName, setClientName] = useState('');
  const [commission, setCommission] = useState('');
  const [passingRate, setPassingRate] = useState('');
  const [patti, setPatti] = useState('');

  // ✅ Memoize userInfo to prevent unnecessary re-renders and infinite loop
  const userInfo = useMemo(() => {
    return JSON.parse(localStorage.getItem('userinfo'));
  }, []);

  // ✅ Memoized API fetch function
  const fetchClientList = useCallback(async () => {
    try {
      const res = await GetAlClientsApi({
        username: userInfo?.username,
        user_id: userInfo?.userId,
      });

      if (res.data.status) {
        setClientList(res.data.data);
      } else {
        setClientList([]);
      }
    } catch (error) {
      console.error('Error fetching client list:', error);
    }
  }, [userInfo]);

  useEffect(() => {
    fetchClientList();
  }, [fetchClientList]);

  const handleClientSelect = (clientName) => {
    setSelectedClientName(clientName);
    const client = clientList.find((c) => c.client_name === clientName);
    if (client) {
      setClientName(client.client_name);
      setCommission(client.lossCommission?.toString() || '');
      setPassingRate(client.passing_rate?.toString() || '');
      setPatti(client.patti?.toString() || '');
    } else {
      setClientName('');
      setCommission('');
      setPassingRate('');
      setPatti('');
    }
  };

  const handleEditClient = async () => {
    if (!selectedClientName.trim() || !passingRate.trim()) {
      toast.error("Please fill all required fields", {
        autoClose: 1500,
        theme: "colored",
        transition: Flip,
      });
      return;
    }

    setLoad(true);
    setLoadColor("#434343");

    try {
      const clientData = {
        username: userInfo?.username,
        user_id: userInfo?.userId,
        client_name: clientName,
        lc: commission !== '' ? Number(commission) : 0,
        passing_rate: passingRate !== '' ? Number(passingRate) : 0,
        patti: patti !== '' ? Number(patti) : 0,
      };

      const res = await EditClientApi(clientData);

      if (res.data.status) {
        setSessionDetail(prev => ({
          ...prev,
          user_clients: res.data.data?.user_clients || [],
        }));

        localStorage.setItem(
          'userinfo',
          JSON.stringify({
            ...userInfo,
            user_clients: res.data.data?.user_clients || [],
          })
        );

        toast.success('Client updated successfully!', {
          autoClose: 3000,
          theme: "colored",
          transition: Flip,
        });

        fetchClientList(); // Refresh list
      } else {
        toast.error(res.data.message, {
          autoClose: 1500,
          theme: "colored",
          transition: Flip,
        });
      }
    } catch (error) {
      toast.error("Something went wrong", {
        autoClose: 1500,
        theme: "colored",
        transition: Flip,
      });
    } finally {
      setLoad(false);
    }
  };

  return (
    <DepositCss>
      <Helmet>
        {/* Page Title */}
        <title>Edit Client | Update Client Details & Rates | Matka Jantri</title>

        {/* SEO Description */}
        <meta
          name="description"
          content="Edit client details and update rates easily on MatkaJantri. Manage your Matka clients with accurate and up-to-date information."
        />

        {/* SEO Directives */}
        <meta name="robots" content="index, follow" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://app.matkajantri.com/edit-client" />

        {/* Favicon and Icons */}
        <link rel="icon" href="/favicon.ico" type="image/x-icon" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png" />
        <link rel="apple-touch-icon" sizes="180x180" href="/apple-touch-icon.png" />

        {/* Mobile Optimization */}
        <meta name="viewport" content="width=device-width, initial-scale=1.0" />

        {/* Open Graph */}
        <meta property="og:title" content="Edit Client | Update Client Details & Rates | MatkaJantri" />
        <meta
          property="og:description"
          content="Edit client details and update rates easily on MatkaJantri. Manage your Matka clients with accurate and up-to-date information."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://app.matkajantri.com" />
        <meta property="og:image" content="https://app.matkajantri.com/Logo.png" />
        <meta name="google-site-verification" content="d6d81656210c6db7" />
      </Helmet>

      <div className="w-100 fullpage">
        <div className="w-3">
          <div className="back-top">
            <i className="fa-solid fa-chevron-left" onClick={() => navigate("/game")}></i>
          </div>
          <div className="w-100 text-center d-flex">
            <button className="addFunds">
              <i className="fa-solid fa-user-pen"></i> Edit Client
            </button>
          </div>

          <div className="w-100 px-3">
            {/* Select Client Dropdown */}
            <div className="mb-3 w-100">
              <label className="form-label">
                Select Client <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                className="form-control"
                value={selectedClientName}
                onChange={(e) => handleClientSelect(e.target.value)}
              >
                <option value="">-- Select Client --</option>
                {clientList.map((client, idx) => (
                  <option key={idx} value={client.client_name}>
                    {client.client_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Read-only client name */}
            {clientName && (
              <div className="mb-3 w-100">
                <label className="form-label">Client Name</label>
                <input
                  type="text"
                  className="form-control ps-2"
                  value={clientName}
                  readOnly
                />
              </div>
            )}

            {/* Editable fields */}
            <div className="mb-3 w-100">
              <label className="form-label">L.C. (Commission%)</label>
              <input
                type="number"
                className="form-control ps-2"
                value={commission}
                onChange={(e) => setCommission(e.target.value)}
              />
            </div>

            <div className="mb-3 w-100">
              <label className="form-label">
                Passing Rate <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                className="form-control ps-2"
                value={passingRate}
                onChange={(e) => setPassingRate(e.target.value)}
              />
            </div>

            <div className="mb-3 w-100">
              <label className="form-label">Patti</label>
              <input
                type="text"
                className="form-control ps-2"
                value={patti}
                onChange={(e) => setPatti(e.target.value)}
              />
            </div>

            <div className="text-center">
              <button
                className="submit-btn support-btn"
                onClick={handleEditClient}
                disabled={!selectedClientName.trim()}
              >
                Update Client
              </button>
            </div>
          </div>

          {/* Client List Table */}
          <div className="w-100 px-3 mt-4">
            <h4 className="mb-3">Client List</h4>
            {clientList.length > 0 ? (
              <div className="table-responsive">
                <table className="table table-bordered table-striped">
                  <thead>
                    <tr>
                      <th>Client Name</th>
                      <th>LC</th>
                      <th>Passing Rate</th>
                      <th>Patti</th>
                      <th>Created At</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientList.map((client) => (
                      <tr key={client._id}>
                        <td>{client.client_name}</td>
                        <td>{client.lossCommission}</td>
                        <td>{client.passing_rate}</td>
                        <td>{client.patti}</td>
                        <td>{new Date(client.createdAt).toLocaleDateString()}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-warning"
                            onClick={() => handleClientSelect(client.client_name)}
                          >
                            <i className="fa fa-edit"></i> Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p>No clients found.</p>
            )}
          </div>
        </div>
      </div>
    </DepositCss>
  );
}
